// Constants
import {
  ANIMATION_CONFIG,
  DEFAULT_MONTHS_OFFSET,
  DEFAULT_PROJECTS_OFFSET,
  MAX_DISPLAY_PROJECTS,
  MONTHS_OFFSET_THRESHOLDS,
  PROJECTS_OFFSET_THRESHOLDS,
} from './constants';
import {
  DIAGONAL_WIPE_TOTAL_DURATION,
  SCENE_3A_3B_OVERLAP_FRAMES,
} from '@/constants';

// Types
import { MonthsOffsets, Project, ProjectsOffsets } from './components/types';

/**
 * Get the most recent projects sorted by joinedAt date in descending order
 * Returns maximum MAX_DISPLAY_PROJECTS projects
 *
 * @param projects - Array of projects to filter and sort
 * @returns Array of most recent projects (max 5)
 */
export const getRecentProjects = (projects: Project[]): Project[] => {
  // First, sort descending to get the most recent projects
  const sortedDesc = [...projects].sort((a, b) => {
    const dateA = a.joinedAt ? new Date(a.joinedAt).getTime() : 0;
    const dateB = b.joinedAt ? new Date(b.joinedAt).getTime() : 0;
    return dateB - dateA; // Descending order (most recent first)
  });

  // Get the top N most recent projects
  const recentProjects = sortedDesc.slice(0, MAX_DISPLAY_PROJECTS);

  // Sort again in ascending order (oldest to newest) for display
  return recentProjects.sort((a, b) => {
    const dateA = a.joinedAt ? new Date(a.joinedAt).getTime() : 0;
    const dateB = b.joinedAt ? new Date(b.joinedAt).getTime() : 0;
    return dateA - dateB; // Ascending order (oldest first)
  });
};

/**
 * Calculate Scene3b duration based on number of projects
 *
 * The duration needs to account for:
 * 1. Animation start delay (animationStartFrame)
 * 2. Time for each batch to display (numberOfBatches * batchDisplayDuration)
 * 3. Buffer for the diagonal wipe transition (transition starts before scene ends)
 *
 * The diagonal wipe starts at: scene3bDuration + SCENE_3A_3B_OVERLAP_FRAMES - DIAGONAL_WIPE_TOTAL_DURATION
 * For the last batch to display fully before transition starts, we need extra buffer.
 *
 * @param projectsCount - Number of projects to display
 * @returns Duration in frames for Scene3b
 */
export const calculateScene3bDuration = (projectsCount: number): number => {
  // Return 0 if no projects - scenes 3a and 3b will be skipped
  if (!projectsCount) {
    return 0;
  }

  const { animationStartFrame, batchDisplayDuration, projectsPerBatch } =
    ANIMATION_CONFIG.projectListComponent;

  // Minimum 1 project, maximum MAX_DISPLAY_PROJECTS to display
  const count = Math.min(MAX_DISPLAY_PROJECTS, Math.max(1, projectsCount));

  // Calculate number of batches needed
  const numberOfBatches = Math.ceil(count / projectsPerBatch);

  // Buffer needed so the last batch displays fully before transition starts
  // diagonalWipeStartLocal = duration + OVERLAP - TOTAL_DURATION
  // We need: diagonalWipeStartLocal >= animationStartFrame + numberOfBatches * batchDisplayDuration
  // So: duration >= animationStartFrame + numberOfBatches * batchDisplayDuration + TOTAL_DURATION - OVERLAP
  const transitionBuffer =
    DIAGONAL_WIPE_TOTAL_DURATION - SCENE_3A_3B_OVERLAP_FRAMES;

  return (
    animationStartFrame +
    numberOfBatches * batchDisplayDuration +
    transitionBuffer
  );
};

/**
 * Returns position offsets for the projects stat block based on the number of projects.
 * Each range (10–19, 20–29, 30–39, …) can have its own tuned values to account
 * for the varying rendered width of the number.
 */
export const getProjectsOffsets = (count: number): ProjectsOffsets => {
  const match = PROJECTS_OFFSET_THRESHOLDS.find(([min]) => count >= min);

  if (!match) {
    return DEFAULT_PROJECTS_OFFSET;
  }

  const [, initialLeftOffset, finalLeftOffset, labelTranslateXOffset] = match;

  return { initialLeftOffset, finalLeftOffset, labelTranslateXOffset };
};

/**
 * Returns position offsets for the months stat block based on the number of months.
 * Each range can have its own tuned values to account
 * for the varying rendered width of the number.
 */
export const getMonthsOffsets = (monthsOnProject: number): MonthsOffsets => {
  const match = MONTHS_OFFSET_THRESHOLDS.find(
    ([min]) => monthsOnProject >= min,
  );

  if (!match) {
    return DEFAULT_MONTHS_OFFSET;
  }

  const [, initialLeftOffset, finalLeftOffset, labelTranslateXOffset] = match;

  return { initialLeftOffset, finalLeftOffset, labelTranslateXOffset };
};
